import { createFileRoute } from "@tanstack/react-router"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { Page } from "@components/Page"
import { Button, Group, Stack, TextInput } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { useTranslation } from "react-i18next"
import { SearchTextInput } from "../-components/search-text-input"
import { CreatePersonModal } from "./-components/create-person-modal"
import {
  InfiniteScrollingTable,
  SortingStateAndUpdater,
} from "@/routes/-components/custom-tanstack-table"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { getPaginatedPersons, useGetMyLicensesSuspense } from "@/orval/gen"
import { usePersonTableColumns } from "./-components/use-person-table-columns"
import { OnChangeFn, SortingState } from "@tanstack/react-table"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../-components/hideable-filter-group"
import { useUserLocalStorage } from "../-components/use-user-local-storage"
import { PersonCategoryTreeSelect } from "@components/tree-select/PersonCategoryTreeSelect"
import { DefaultInfiniteList } from "@components/InfiniteList"
import { PersonCard } from "./-components/person-card"
import { DefaultListItem } from "@components/default-list/list-item"

export interface Search {
  searchString?: string
  sorting?: SortingState
  personCategoryTreeNodes?: string[]
  comValue?: string
}

export const Route = createFileRoute("/app/contacts/")({
  component: PersonsPage,
  validateSearch: (search: Search) => ({
    ...search,
    sorting: search.sorting ?? [{ id: "lastName", desc: false }],
  }),
})

export function PersonsPage() {
  const search = Route.useSearch()
  const { searchString } = search

  const { xs } = useBreakpoints()
  const navigate = Route.useNavigate()
  const navigateToPersonId = (personId: number) => {
    navigate({
      to: "/app/contacts/$id",
      params: {
        id: personId.toString(),
      },
      search: { tab: "start" },
    })
  }
  function updateSearch(input: Partial<typeof search>) {
    navigate({
      search: {
        ...search,
        ...input,
      },
      replace: true,
    })
  }
  const [
    createModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure()
  const { t } = useTranslation("app_server")
  const [filterGroupVisible, setFilterGroupVisible] = useUserLocalStorage({
    key: "contacts-filter-group-visible",
    defaultValue: false,
  })
  const queryOptions = useResponsiveFilterQueryOptions(filterGroupVisible)
  const queryResult = useDefaultInfiniteQuery(
    getPaginatedPersons,
    (pageRequest) => ({
      ...pageRequest,
      ...search,
    }),
    queryOptions
  )
  const { baseColumns, extendedColumns, defaultColumnVisibility } =
    usePersonTableColumns()

  const setSortingState: OnChangeFn<SortingState> = (updater) => {
    const newSortingState =
      typeof updater === "function" ? updater(search.sorting ?? []) : updater
    updateSearch({ sorting: newSortingState })
  }
  const sortingState: SortingStateAndUpdater = [search.sorting, setSortingState]
  const filterProps = { w: xs ? "100%" : 300 }
  const { data: userLicenses } = useGetMyLicensesSuspense()
  const isManager = React.useMemo(
    () =>
      userLicenses.some(
        (l) =>
          l.product === "theaPersoWebManager" ||
          l.product === "theaDispoWebManager"
      ),
    [userLicenses]
  )
  const columns = React.useMemo(
    () => (isManager ? extendedColumns : baseColumns),
    [baseColumns, extendedColumns, isManager]
  )
  return (
    <Page
      buttonGroupProps={{
        children: (
          <>
            <Button onClick={openCreateModal}>{t("CreatePerson")}</Button>
            <ResponsiveFilterGroup.Button
              onChange={setFilterGroupVisible}
              visible={filterGroupVisible}
              filters={{
                personCategories: search.personCategoryTreeNodes,
                contactData: xs ? undefined : search.comValue,
              }}
            />
          </>
        ),
      }}
    >
      <Stack h={"100%"}>
        <Group>
          <SearchTextInput
            {...filterProps}
            value={searchString}
            onChange={(e) =>
              updateSearch({
                searchString: e.target.value,
              })
            }
          />
          <ResponsiveFilterGroup
            visible={filterGroupVisible}
            setVisible={setFilterGroupVisible}
            filters={{
              personCategories: search.personCategoryTreeNodes,
              contactData: xs ? undefined : search.comValue,
            }}
            onReset={() =>
              updateSearch({
                personCategoryTreeNodes: undefined,
                comValue: undefined,
              })
            }
          >
            <PersonCategoryTreeSelect
              {...filterProps}
              value={search.personCategoryTreeNodes}
              onChange={(value) =>
                updateSearch({ personCategoryTreeNodes: value })
              }
            />
            {!xs && isManager && (
              <TextInput
                {...filterProps}
                label={t("ContactData")}
                value={search.comValue}
                onChange={(e) =>
                  updateSearch({
                    comValue: e.target.value,
                  })
                }
              />
            )}
          </ResponsiveFilterGroup>
        </Group>
        {xs ? (
          <DefaultInfiniteList
            queryResult={queryResult}
            renderItem={({ entity: person }) => (
              <DefaultListItem>
                <PersonCard
                  globalFilter={searchString ?? ""}
                  onClickItem={(person) => navigateToPersonId(person.personId)}
                  person={person}
                />
              </DefaultListItem>
            )}
          />
        ) : (
          <InfiniteScrollingTable
            id="person-table"
            infiniteQueryResult={queryResult}
            getRowProps={(row) => ({
              onClick: () => navigateToPersonId(row.original.personId),
            })}
            columnDefinitions={columns}
            defaultColumnVisibility={defaultColumnVisibility}
            sortingState={sortingState}
          />
        )}
      </Stack>
      <CreatePersonModal
        opened={createModalOpened}
        onClose={closeCreateModal}
        onPersonCreated={navigateToPersonId}
      />
    </Page>
  )
}
